import { Fragment, useEffect, useState } from "react";
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { useTranslation } from "react-i18next";

import i18n from "../pages/i18n";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const languageLabels = {
  fr: "Français",
  en: "English",
  nl: "Nederlands",
};
const languageOptions = [
  { code: "fr", label: "Français" },
  { code: "nl", label: "Nederlands" },
  { code: "en", label: "English" },
];

export default function LanguageDropdown(props) {
  const { i18n: activeI18n } = useTranslation();
  const detectedLanguage = (
    activeI18n.resolvedLanguage ||
    activeI18n.language ||
    "fr"
  ).slice(0, 2);
  const initialLanguage = languageLabels[detectedLanguage]
    ? detectedLanguage
    : "fr";
  const normalizedInitialLanguage = ["fr", "en", "nl"].includes(
    initialLanguage
  )
    ? initialLanguage
    : "fr";

  const [lng, setLng] = useState(normalizedInitialLanguage);
  const [btn, setBtn] = useState(languageLabels[normalizedInitialLanguage]);

  useEffect(() => {
    const nextLabel = languageLabels[lng] || languageLabels.fr;
    setBtn(nextLabel);

    if (i18n.language !== lng) {
      i18n.changeLanguage(lng);
    }
  }, [lng]);

  useEffect(() => {
    const externalLanguage = (
      activeI18n.resolvedLanguage ||
      activeI18n.language ||
      "fr"
    ).slice(0, 2);

    if (externalLanguage && externalLanguage !== lng) {
      setLng(externalLanguage);
    }
  }, [activeI18n.language, activeI18n.resolvedLanguage, lng]);

  return (
    <Menu
      as="div"
      className={classNames(
        "relative inline-block text-left font-display",
        props.className
      )}
    >
      <div>
        <MenuButton
          className={classNames(
            "flex w-full items-center justify-center gap-x-1.5 rounded-full px-3 py-2 text-sm font-semibold text-green-500 transition-colors duration-200 hover:bg-gray-50",
            props.buttonClassName
          )}
        >
          {btn}
          <ChevronDownIcon
            className="-mr-1 h-4 w-4 text-gray-400"
            aria-hidden="true"
          />
        </MenuButton>
      </div>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <MenuItems
          className={classNames(
            "absolute right-0 z-10 mt-2 w-40 rounded-2xl bg-white shadow-lg focus:outline-none",
            props.menuClassName
          )}
        >
          <div className="space-y-1 p-1">
            {languageOptions.map((option) => (
              <MenuItem key={option.code}>
                {({ active }) => (
                  <button
                    type="button"
                    onClick={() => setLng(option.code)}
                    className={classNames(
                      "block w-full rounded-xl px-4 py-2.5 text-left text-sm transition-colors duration-150",
                      lng === option.code
                        ? "bg-[#43A047]/12 font-semibold text-[#2f7d32]"
                        : "text-slate-700",
                      active ? "rounded-xl bg-[#43A047]/10 text-[#2f7d32]" : "",
                      props.itemClassName
                    )}
                  >
                    {option.label}
                  </button>
                )}
              </MenuItem>
            ))}
          </div>
        </MenuItems>
      </Transition>
    </Menu>
  );
}
